import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import EmbeddedChatSection from '../sections/EmbeddedChatSection';
import { api } from '../services/api';
import packageJson from '../../package.json';
import InfoIcon from '@mui/icons-material/Info';

const style = {
  headerStyle: {
    height: 64,
    backgroundColor: '#E2E2E5',
  },
  bannerError: {
    width: 482 + 24,
    height: 82 + 24,
    backgroundColor: '#FFDAD6',
    borderRadius: 16,
    padding: 24,
  },
};

function EmbeddedChat({ logger }) {
  const [view, setView] = useState('chat');
  const [desktopMode, setDesktopMode] = useState(window.matchMedia('(min-width: 600px)').matches);
  const [backendVersion, setBackendVersion] = useState(false);
  const [backendReleased, setBackendReleased] = useState(false);
  const [chatTitle, setChatTitle] = useState('');
  const [chatIntro, setChatIntro] = useState('');
  const [firstResponseFooter, setFirstResponseFooter] = useState('');
  const [versionText, setVersionText] = useState('');
  const [surveyToolsAllowed, setSurveyToolsAllowed] = useState(false);
  const [responseIconUrl, setResponseIconUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const iframeLocation = useLocation();
  const queryParams = new URLSearchParams(iframeLocation.search);
  const anonGroup = queryParams.get('anon_group');
  const noTitle = queryParams.get('no_title');
  // set the chat location to the passed parent location, if present, otherwise to our current page
  let chatLocation = queryParams.get('location');
  if (!chatLocation) {
    const ourUrl = new URL(iframeLocation.pathname, window.location.origin);
    chatLocation = ourUrl.host + ourUrl.pathname;
  }

  const calculateVersionText = (backendVersion, backendReleased) => {
    const frontDate = new Date(process.env.REACT_APP_BUILD_DATE);
    const backendDate = backendReleased ? new Date(backendReleased) : frontDate;
    const maxDate = new Date(Math.max(backendDate, frontDate));
    const dateStr = maxDate.toISOString().split('T')[0];
    const versionText = `v${packageJson.version}/${backendVersion || ''} released ${dateStr}`;
    setVersionText(versionText);
  };

  // handle smart auto-scrolling
  const messagesEndRef = useRef(null);
  let animationFrameId = null;
  let scrollUpEvents = 0;
  let autoScrollCanceled = false;

  const scrollToBottom = () => {
    if (!messagesEndRef.current) {
      return;
    }

    // calculate the change needed to scroll to the bottom
    const element = messagesEndRef.current;
    const start = element.scrollTop;
    const end = element.scrollHeight - element.clientHeight;
    const change = end - start;

    // if the scroll position is already there, reset autoScrollCanceled and return
    if (change <= 0) {
      autoScrollCanceled = false;
      scrollUpEvents = 0;
      return;
    }

    // if auto-scroll has been canceled, return immediately
    if (autoScrollCanceled) {
      return;
    }

    // if we're already in the middle of auto-scrolling, cancel that prior animation
    if (animationFrameId) {
      window.cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }

    // set up the auto-scroll animation, for a given duration in ms
    const duration = 250;
    let startTimestamp = null;
    let lastScrollTop = start;

    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const currentScrollTop = element.scrollTop;

      // if we see we've scrolled up, skip this frame and cancel auto-scroll if we cancel three frames in a row
      if (currentScrollTop < lastScrollTop) {
        if (++scrollUpEvents >= 3) {
          animationFrameId = null;
          autoScrollCanceled = true;
        }
        lastScrollTop = currentScrollTop;
      }
      else {
        element.scrollTop = start + change * progress;
        lastScrollTop = element.scrollTop;
      }
      // if we're not done scrolling, request the next frame
      if (progress < 1) {
        animationFrameId = window.requestAnimationFrame(step);
      }
      else {
        animationFrameId = null;
      }
    };

    // launch the auto-scroll animation
    animationFrameId = window.requestAnimationFrame(step);
  };

  const beginAutoScroll = () => {
    autoScrollCanceled = false;
    scrollUpEvents = 0;
    scrollToBottom();
  };

  useEffect(() => {
    window.matchMedia('(min-width: 600px)').addEventListener('change', e => setDesktopMode(e.matches));
  }, []);

  useEffect(() => {
    const getAlmitraParams = async () => {
      setLoading(true);
      try {
        const response = await api.getAlmitraParams(anonGroup);

        if (response.data) {
          setBackendVersion(response.data.options.version);
          setBackendReleased(response.data.options.build_date);
          setChatIntro(response.data.options.chat_intro);
          setFirstResponseFooter(response.data.options.first_response_footer);
          setChatTitle(response.data.options.chat_title);
          setSurveyToolsAllowed(response.data.options?.survey_tools_allowed ?? false);
          setResponseIconUrl(response.data.options?.response_icon_url ?? null);
          setLoading(false);
        }
        else {
          setError('Failed to retrieve parameter data from back-end');
        }
      } catch (error) {
        console.log(error.message);
        setError(error.message);
      }
    };
    getAlmitraParams();
  }, []);

  useEffect(() => {
    calculateVersionText(backendVersion, backendReleased);
  }, [backendVersion, backendReleased]);

  const renderChat = () => {
    return (
      <div>
        <EmbeddedChatSection
          logger={logger}
          view={view}
          setView={setView}
          desktopMode={desktopMode}
          messagesEndRef={messagesEndRef}
          scrollToBottom={scrollToBottom}
          beginAutoScroll={beginAutoScroll}
          versionText={versionText}
          chatTitle={chatTitle}
          chatIntro={chatIntro}
          firstResponseFooter={firstResponseFooter}
          anonGroup={anonGroup}
          noTitle={noTitle === '1' || noTitle === 'true' || noTitle === 'yes'}
          chatLocation={chatLocation}
          surveyToolsAllowed={surveyToolsAllowed}
          responseIconUrl={responseIconUrl}
        />
      </div>
    );
  };

  const renderLoading = () => {
    return (
      <div
        style={{
          position: 'fixed', // Fixed position
          top: '0', // Place at the top
          left: '0', // Place at the left
          height: '100%', // Cover entire vertical height
          width: '100%', // Cover entire horizontal width
          backgroundColor: 'rgba(255, 255, 255, 0.8)', // Optional, white background with opacity
          zIndex: 9999, // Make sure it covers other items
          display: 'flex', // Enable flexbox
          justifyContent: 'center', // Center horizontally
          alignItems: 'center', // Center vertically
          flexDirection: 'column',
        }}
      >
        {error === '' ? (
          <>
            <CircularProgress />
            <div style={{ marginTop: '10px' }}>Loading...</div>
          </>
        ) : (
          <div style={style.bannerError}>
            <div
              style={{
                height: '100%',
                width: '100%',
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <InfoIcon style={{ color: '#BA1A1A', fontSize: 40 }} />
              <div style={{ marginTop: '10px', color: '#BA1A1A', fontSize: 16 }}>
                Error: {error}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) return renderLoading();
  return renderChat();
}

export default EmbeddedChat;
